import { useEffect, useMemo, useRef, useState } from "react";
import type {
  GlobalArtikel,
  Kostenart,
  Stueckliste,
} from "@bidi-handel/core/schemas";

/**
 * Editor für die Montage-Stückliste eines Artikelstamm-Artikels (Set).
 *
 * Komponenten verweisen per Combobox auf andere Katalog-Artikel (Preis entsteht
 * dann LIVE in der Bepreisung) ODER sind freie Zeilen (z.B. Montage-Lohn) mit
 * eigenem VK. `preisModus`:
 *  - summiert: Setpreis = Σ Komponenten.
 *  - komplettpreis: fester Setpreis, Komponenten nur EFB-223-Aufgliederung.
 *
 * Controlled-Light: hält die editierbaren Zeilen lokal (mit React-Keys) und
 * meldet das fertige `Stueckliste`-Objekt (oder `null` = keine/entfernt) per
 * `onChange` an den Dialog, der es in den PATCH übernimmt.
 */

const KOSTENARTEN: Kostenart[] = ["material", "lohn", "geraet", "sonstige", "fremd"];

interface EditRow {
  localId: string;
  artikelCode: string | null;
  bezeichnung: string;
  menge: string;
  einheit: string;
  kostenart: Kostenart;
  einzelpreis: string;
  bemerkung: string;
}

let rowSeq = 0;
function newLocalId(): string {
  rowSeq += 1;
  return `sl-row-${rowSeq}`;
}

function fromStueckliste(sl: Stueckliste | null | undefined): EditRow[] {
  if (!sl) return [];
  return sl.komponenten.map((k) => ({
    localId: newLocalId(),
    artikelCode: k.artikelCode,
    bezeichnung: k.bezeichnung,
    menge: String(k.menge),
    einheit: k.einheit,
    kostenart: k.kostenart,
    einzelpreis: k.artikelCode ? "" : String(k.einzelpreis ?? 0),
    bemerkung: k.bemerkung ?? "",
  }));
}

function emptyRow(): EditRow {
  return {
    localId: newLocalId(),
    artikelCode: null,
    bezeichnung: "",
    menge: "1",
    einheit: "St",
    kostenart: "material",
    einzelpreis: "0",
    bemerkung: "",
  };
}

export function StuecklisteEditor({
  value,
  onChange,
  katalogArtikel,
}: {
  value: Stueckliste | null | undefined;
  onChange: (next: Stueckliste | null) => void;
  katalogArtikel: GlobalArtikel[];
}) {
  const [rows, setRows] = useState<EditRow[]>(() => fromStueckliste(value));
  const [preisModus, setPreisModus] = useState<"summiert" | "komplettpreis">(
    value?.preisModus ?? "summiert"
  );
  const [komplettpreis, setKomplettpreis] = useState(
    value?.komplettpreis !== undefined ? String(value.komplettpreis) : ""
  );

  // Fertiges Stueckliste-Objekt (oder null) an den Dialog melden, wenn sich
  // etwas ändert. onChange via Ref, damit die Effekt-Deps stabil bleiben.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const komponenten = rows
      .filter((r) => r.bezeichnung.trim() !== "" && Number(r.menge) > 0)
      .map((r) => ({
        artikelCode: r.artikelCode,
        bezeichnung: r.bezeichnung.trim(),
        menge: Number(r.menge),
        einheit: r.einheit.trim() || "St",
        kostenart: r.kostenart,
        einzelpreis: r.artikelCode ? 0 : Number(r.einzelpreis) || 0,
        bemerkung: r.bemerkung.trim() || undefined,
      }));
    if (komponenten.length === 0) {
      onChangeRef.current(null);
      return;
    }
    onChangeRef.current({
      komponenten,
      preisModus,
      komplettpreis:
        preisModus === "komplettpreis" ? Number(komplettpreis) || 0 : undefined,
    });
  }, [rows, preisModus, komplettpreis]);

  function update(localId: string, patch: Partial<EditRow>) {
    setRows((cur) => cur.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));
  }
  function remove(localId: string) {
    setRows((cur) => cur.filter((r) => r.localId !== localId));
  }

  const hatKomponenten = rows.length > 0;

  return (
    <div className="mt-4 rounded-lg border border-slate-300 border-l-2 border-l-brand-400 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">
          Stückliste / Set
          <span className="ml-2 normal-case tracking-normal text-slate-400">
            {hatKomponenten ? `· ${rows.length} Komponente(n)` : "· kein Set"}
          </span>
        </h4>
        {hatKomponenten && (
          <button
            type="button"
            onClick={() => setRows([])}
            className="text-xs font-medium text-rose-600 hover:underline"
          >
            Set entfernen
          </button>
        )}
      </div>

      {hatKomponenten && (
        <>
          <div className="grid grid-cols-[1fr_84px_72px_120px_96px_28px] gap-2 px-1 pb-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
            <span>Komponente / Artikel</span>
            <span>Menge</span>
            <span>Einheit</span>
            <span>Kostenart</span>
            <span className="text-right">Preis</span>
            <span />
          </div>
          <div className="space-y-2">
            {rows.map((r) => (
              <KomponentenRow
                key={r.localId}
                row={r}
                katalogArtikel={katalogArtikel}
                onUpdate={(patch) => update(r.localId, patch)}
                onRemove={() => remove(r.localId)}
              />
            ))}
          </div>
        </>
      )}

      <button
        type="button"
        onClick={() => setRows((cur) => [...cur, emptyRow()])}
        className="mt-2 rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white"
      >
        + Komponente
      </button>

      {hatKomponenten && (
        <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-slate-200 pt-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <input
              type="radio"
              name="preisModus"
              checked={preisModus === "summiert"}
              onChange={() => setPreisModus("summiert")}
            />
            Summiert (Σ Komponenten)
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <input
              type="radio"
              name="preisModus"
              checked={preisModus === "komplettpreis"}
              onChange={() => setPreisModus("komplettpreis")}
            />
            Komplettpreis
          </label>
          {preisModus === "komplettpreis" && (
            <label className="flex items-center gap-1.5 text-xs text-slate-700">
              fester EP (EUR/Einheit)
              <input
                type="number"
                min={0}
                step={0.01}
                value={komplettpreis}
                onChange={(e) => setKomplettpreis(e.currentTarget.value)}
                className="input sm w-28"
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

function KomponentenRow({
  row,
  katalogArtikel,
  onUpdate,
  onRemove,
}: {
  row: EditRow;
  katalogArtikel: GlobalArtikel[];
  onUpdate: (patch: Partial<EditRow>) => void;
  onRemove: () => void;
}) {
  const istArtikel = row.artikelCode !== null;
  return (
    <div className="grid grid-cols-[1fr_84px_72px_120px_96px_28px] items-center gap-2">
      {/* Komponente: Artikel-Picker oder freie Bezeichnung */}
      {istArtikel ? (
        <div className="flex items-center gap-1 truncate">
          <code className="rounded bg-brand-50 px-1.5 py-0.5 font-mono text-xs text-brand-700">
            {row.artikelCode}
          </code>
          <span className="truncate text-xs text-slate-600">{row.bezeichnung}</span>
          <button
            type="button"
            title="Artikel-Bindung lösen (freie Zeile)"
            onClick={() => onUpdate({ artikelCode: null })}
            className="text-xs text-slate-400 hover:text-rose-600"
          >
            ✕
          </button>
        </div>
      ) : (
        <ArtikelPicker
          katalogArtikel={katalogArtikel}
          bezeichnung={row.bezeichnung}
          onBezeichnung={(v) => onUpdate({ bezeichnung: v })}
          onPick={(a) =>
            onUpdate({
              artikelCode: a.code,
              bezeichnung: a.bezeichnung,
              einheit: a.einheit ?? row.einheit,
            })
          }
        />
      )}

      <input
        type="number"
        min={0}
        step="any"
        value={row.menge}
        onChange={(e) => onUpdate({ menge: e.currentTarget.value })}
        className="input sm"
        title="Menge pro 1 Einheit des Sets"
        aria-label="Menge"
      />
      <input
        type="text"
        value={row.einheit}
        onChange={(e) => onUpdate({ einheit: e.currentTarget.value })}
        className="input sm"
        aria-label="Einheit"
      />
      <select
        value={row.kostenart}
        onChange={(e) => onUpdate({ kostenart: e.currentTarget.value as Kostenart })}
        className="input sm"
        aria-label="Kostenart"
      >
        {KOSTENARTEN.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>
      {/* Preis: nur freie Zeilen pflegen einen VK; Artikel-Zeilen sind live. */}
      {istArtikel ? (
        <span className="text-right text-xs italic text-slate-400" title="Preis live aus Artikel">
          live
        </span>
      ) : (
        <input
          type="number"
          min={0}
          step={0.01}
          value={row.einzelpreis}
          onChange={(e) => onUpdate({ einzelpreis: e.currentTarget.value })}
          className="input sm"
          title="VK pro Einheit (freie Zeile)"
          aria-label="Einzelpreis"
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="text-slate-400 hover:text-rose-600"
        title="Komponente entfernen"
      >
        ✕
      </button>
    </div>
  );
}

/** Freie Bezeichnung mit Dropdown-Vorschlägen aus dem Katalog. Auswahl bindet
 *  die Zeile an einen Artikel; einfaches Tippen lässt sie frei. */
function ArtikelPicker({
  katalogArtikel,
  bezeichnung,
  onBezeichnung,
  onPick,
}: {
  katalogArtikel: GlobalArtikel[];
  bezeichnung: string;
  onBezeichnung: (v: string) => void;
  onPick: (a: GlobalArtikel) => void;
}) {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = bezeichnung.trim().toLowerCase();
    if (!q) return katalogArtikel.slice(0, 12);
    return katalogArtikel
      .filter(
        (a) =>
          a.code.toLowerCase().includes(q) ||
          a.bezeichnung.toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [katalogArtikel, bezeichnung]);

  return (
    <div className="relative">
      <input
        type="text"
        value={bezeichnung}
        onChange={(e) => {
          onBezeichnung(e.currentTarget.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Artikel suchen oder freie Zeile tippen …"
        className="input sm w-full"
      />
      {open && filtered.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-10 mt-0.5 max-h-56 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {filtered.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                onPick(a);
                setOpen(false);
              }}
              className="block w-full truncate px-2.5 py-1.5 text-left text-xs hover:bg-slate-100"
            >
              <code className="font-mono text-brand-700">{a.code}</code>
              <span className="ml-2 text-slate-600">{a.bezeichnung}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
